import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from './apiUtils';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { CheckCircle2, XCircle, Calendar, CreditCard, Loader2, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';

const Subscription = () => {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [billingCycle, setBillingCycle] = useState('MONTHLY'); // 'MONTHLY' or 'YEARLY'
  const [showReminder, setShowReminder] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, subscriptionRes, historyRes] = await Promise.all([
        apiGet('/api/razorpay/plans'),
        apiGet('/api/razorpay/subscription'),
        apiGet('/api/razorpay/payment-history?limit=5'),
      ]);

      const plansData = await plansRes.json();
      const subscriptionData = await subscriptionRes.json();
      const historyData = await historyRes.json();

      setPlans(plansData.plans || []);
      setCurrentSubscription(subscriptionData.subscription);
      setShowReminder(subscriptionData.showReminder || false);
      setPaymentHistory(historyData.payments || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubscribe = async (plan) => {
    try {
      setProcessingPayment(true);

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        return;
      }

      // Create order
      const orderResponse = await apiPost('/api/razorpay/create-order', { planId: plan.id });
      const orderData = await orderResponse.json();

      if (!orderResponse.ok || !orderData.success) {
        throw new Error(orderData.message || 'Failed to create order');
      }

      const { order, razorpayKeyId } = orderData;

      console.log('📋 Order details:', {
        orderId: order.orderId,
        planType: order.planDetails.type,
        amount: order.amount
      });

      console.log('💳 Creating Razorpay one-time payment checkout');

      // Configure Razorpay options - all payments are one-time now
      const options = {
        key: razorpayKeyId,
        name: 'OPENARC',
        description: `${order.planDetails.name} Subscription`,
        order_id: order.orderId,
        amount: order.amount,
        currency: order.currency,
        handler: async (response) => {
          try {
            const orderId = response.razorpay_order_id;
            
            // Verify payment (works for both orders and subscriptions)
            const verifyResponse = await apiPost('/api/razorpay/verify-payment', {
              orderId: orderId,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });

            const verifyData = await verifyResponse.json();

            if (verifyResponse.ok && verifyData.success) {
              toast.success('🎉 Payment successful! Your subscription is now active.');
              // Reload page to update subscription status across the app
              setTimeout(() => window.location.reload(), 1500);
              fetchData(); // Refresh data
            } else {
              throw new Error(verifyData.message || 'Verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed. Please contact support.');
          } finally {
            setProcessingPayment(false);
          }
        },
        prefill: {
          name: '',
          email: '',
          contact: '',
        },
        readonly: {
          contact: false,
          email: false,
          name: false,
        },
        remember_customer: true,
        theme: {
          color: '#7c3aed',
        },
        modal: {
          ondismiss: () => {
            setProcessingPayment(false);
            toast.info('Payment cancelled');
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', async (response) => {
        console.error('Payment failed:', response);
        
        // Record failed payment
        await apiPost('/api/razorpay/payment-failed', {
          orderId: order.orderId,
          errorCode: response.error.code,
          errorDescription: response.error.description,
        });

        toast.error(`Payment failed: ${response.error.description}`);
        setProcessingPayment(false);
      });

      razorpay.open();
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast.error('Failed to initiate payment');
      setProcessingPayment(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      ACTIVE: 'bg-green-500',
      PENDING: 'bg-yellow-500',
      CANCELLED: 'bg-red-500',
      EXPIRED: 'bg-gray-500',
      CAPTURED: 'bg-green-500',
      FAILED: 'bg-red-500',
      CREATED: 'bg-blue-500',
    };

    // Display "PAID" instead of "CAPTURED" for better UX
    const displayStatus = status === 'CAPTURED' ? 'PAID' : status;

    return (
      <Badge className={`${statusColors[status] || 'bg-gray-500'} text-white`}>
        {displayStatus}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Subscription Management</h1>
        <p className="text-gray-600">Manage your subscription plans and billing</p>
      </div>

      {/* Payment Reminder Banner - 2 days before expiry */}
      {showReminder && (
        <div className="mb-6 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-400 rounded-xl p-6 shadow-lg">
          <div className="flex items-center gap-4">
            <div className="bg-red-500 rounded-full p-3 shadow-md">
              <XCircle className="h-7 w-7 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-red-900 mb-1">Payment Reminder</h3>
              <p className="text-base text-gray-700">
                Your subscription expires on <strong>{currentSubscription && new Date(currentSubscription.end_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</strong>. Please pay to continue access.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Subscription Status */}
      {currentSubscription ? (
        // Check if subscription is still valid (end_date in future)
        new Date(currentSubscription.end_date) > new Date() ? (
          // Active Subscription - unified display for all plan types
          <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-500 rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="bg-green-500 rounded-full p-3 shadow-md">
                  <CheckCircle2 className="h-7 w-7 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-green-900">ACTIVE SUBSCRIPTION</h3>
                    <Badge className="bg-green-500 text-white text-sm">
                      {currentSubscription.plan_name}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Calendar className="h-4 w-4" />
                      <span>Started: <span className="font-semibold text-green-900">{formatDate(currentSubscription.start_date)}</span></span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Calendar className="h-4 w-4" />
                      <span>Expires on: <span className="font-bold text-green-900">{formatDate(currentSubscription.end_date)}</span></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Subscription Expired
          <div className="mb-8 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-400 rounded-xl p-6 shadow-lg">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="bg-red-500 rounded-full p-3 shadow-md">
                <XCircle className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-red-900 mb-2">Subscription Expired</h3>
                <p className="text-base text-gray-700 mb-3">
                  Your subscription expired on <strong>{formatDate(currentSubscription.end_date)}</strong>. Please renew to continue access.
                </p>
                <p className="text-sm text-gray-600 bg-white/50 rounded-lg py-2 px-4 inline-block">
                  For any queries, contact us: <strong>9176415610</strong> / <strong>7995328686</strong>
                </p>
              </div>
            </div>
          </div>
        )
      ) : (
        // State 3: No Subscription Ever
        <div className="mb-8 flex justify-center">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-400 rounded-xl p-6 shadow-lg max-w-2xl w-full">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="bg-amber-500 rounded-full p-3 shadow-md">
                <XCircle className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-amber-900 mb-2">Payment Reminder</h3>
                <p className="text-base text-gray-700 mb-3">
                  Dear Customer, please subscribe to access all features and enjoy uninterrupted service.
                </p>
                <p className="text-sm text-gray-600 bg-white/50 rounded-lg py-2 px-4 inline-block">
                  For any queries, contact us: <strong>9176415610</strong> / <strong>7995328686</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Plans */}
      <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Choose Your Plan</h2>
          <div className="flex justify-center">
            <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Subscription Plan</CardTitle>
              <CardDescription className="text-center">
                Select your billing cycle
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Billing Cycle Radio Buttons */}
              <div className="space-y-3">
                {plans.map((plan) => {
                  const isSelected = billingCycle === plan.plan_type;
                  const isCurrentPlan = currentSubscription && 
                                       currentSubscription.plan_id === plan.id &&
                                       new Date(currentSubscription.end_date) >= new Date();
                  const monthlyPrice = plan.plan_type === 'MONTHLY' ? plan.amount : Math.round(plan.amount / 12);
                  
                  // Calculate savings dynamically
                  const monthlyPlanAmount = plans.find(p => p.plan_type === 'MONTHLY')?.amount || 2999;
                  const yearlySavings = (monthlyPlanAmount * 12) - plan.amount;
                  
                  return (
                    <div
                      key={plan.id}
                      onClick={() => setBillingCycle(plan.plan_type)}
                      className={`
                        relative flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all
                        ${isCurrentPlan ? 'border-green-500 bg-green-50' : 
                          isSelected ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-gray-300'}
                      `}
                    >
                      {/* Current Plan Badge */}
                      {isCurrentPlan && (
                        <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                          Current Plan
                        </div>
                      )}
                      
                      <div className="flex items-center gap-3">
                        <div className={`
                          w-5 h-5 rounded-full border-2 flex items-center justify-center
                          ${isCurrentPlan ? 'border-green-500' : isSelected ? 'border-violet-500' : 'border-gray-300'}
                        `}>
                          {isSelected && (
                            <div className={`w-3 h-3 rounded-full ${isCurrentPlan ? 'bg-green-500' : 'bg-violet-500'}`} />
                          )}
                        </div>
                        <div>
                          <div className="font-semibold">
                            {plan.plan_type === 'MONTHLY' ? 'Monthly' : 'Yearly'}
                          </div>
                          <div className="text-sm text-gray-600">
                            {plan.plan_type === 'YEARLY' && (
                              <span className="text-green-600 font-medium">
                                Save ₹{yearlySavings.toLocaleString()} • One-time payment for 1 year
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-baseline gap-1">
                          <IndianRupee className="h-5 w-5" />
                          <span className="text-2xl font-bold">{plan.amount.toLocaleString()}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {plan.plan_type === 'YEARLY' ? (
                            <span>for 1 year (₹{monthlyPrice.toLocaleString()}/month)</span>
                          ) : (
                            <span>per month</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Separator />

              {/* Summary */}
              <div className="bg-gray-50 p-5 rounded-lg space-y-3">
                {(() => {
                  // Calculate start date: if there's an active subscription, start from its end date
                  // Otherwise start from today
                  let startDate;
                  const hasActiveSubscription = currentSubscription && new Date(currentSubscription.end_date) >= new Date();
                  
                  if (hasActiveSubscription) {
                    startDate = new Date(currentSubscription.end_date);
                  } else {
                    startDate = new Date();
                  }
                  
                  // Calculate end date based on billing cycle
                  const endDate = new Date(startDate);
                  if (billingCycle === 'MONTHLY') {
                    endDate.setMonth(endDate.getMonth() + 1);
                  } else {
                    endDate.setFullYear(endDate.getFullYear() + 1);
                  }
                  
                  const selectedPlan = plans.find(p => p.plan_type === billingCycle);
                  const isCurrentPlan = hasActiveSubscription && 
                                       selectedPlan &&
                                       currentSubscription.plan_id === selectedPlan.id;
                  
                  return (
                    <>
                      {/* Plan switch notice */}
                      {hasActiveSubscription && !isCurrentPlan && (
                        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800">
                          <span className="font-semibold">📅 Plan Switch:</span> Your new subscription will start on {formatDate(startDate)} (after your current plan ends)
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-gray-600 text-left">Billing Cycle:</span>
                        <span className="font-medium text-right">
                          {billingCycle === 'MONTHLY' ? 'Monthly' : 'Yearly'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-gray-600 text-left">Start Date:</span>
                        <span className="font-medium text-right">
                          {formatDate(startDate)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-gray-600 text-left">End Date:</span>
                        <span className="font-medium text-right">
                          {formatDate(endDate)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-gray-600 text-left">Billing Type:</span>
                        <span className="font-medium text-right">
                          {billingCycle === 'MONTHLY' ? 'One-time (1 month)' : 'One-time (1 year)'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-gray-600 text-left">Usage Period:</span>
                        <span className="font-medium text-right">
                          {billingCycle === 'MONTHLY' ? '1 month' : '1 year'}
                        </span>
                      </div>
                      <Separator className="my-2" />
                      <div className="grid grid-cols-2 gap-2 items-center">
                        <span className="font-semibold text-lg text-left">Total Amount:</span>
                        <div className="flex items-baseline gap-1 justify-end">
                          <IndianRupee className="h-5 w-5" />
                          <span className="text-2xl font-bold">
                            {plans.find(p => p.plan_type === billingCycle)?.amount.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </CardContent>
            <CardFooter className="pt-6">
              <Button
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold shadow-lg"
                size="lg"
                disabled={processingPayment}
                onClick={() => {
                  const plan = plans.find(p => p.plan_type === billingCycle);
                  if (plan) handleSubscribe(plan);
                }}
              >
                {processingPayment ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-5 w-5" />
                    Subscribe Now
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>

      {/* Payment History */}
      {paymentHistory.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold mb-6">Payment History</h2>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        Date
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        Plan
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        Amount
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        Status
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        Payment ID
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paymentHistory.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          {formatDate(payment.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">
                          {payment.plan_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-center">
                          {formatAmount(payment.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {getStatusBadge(payment.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono text-center">
                          {payment.razorpay_payment_id || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
};

export default Subscription;

